import pick from 'lodash/pick'
import type { ExtraVariableRow, RCollectionSuggestion } from './collection-configuration-types'

const SAVED_KEYS = [
  'variable_name',
  'variable_instructions',
  'variable_format_instructions',
  'variable_type',
  'variable_enum_values',
  'configure_data_format',
  'overrides',
  'source',
] as const

const nameOf = (row: ExtraVariableRow) => (row.variable_name ?? '').trim()

/** The runner's Scribe fills extra rows on a call only, and a later step can use them only there. */
export const takesExtraVariables = (channel?: string) => channel === 'call'

export const extraAuthoredOf = (row: ExtraVariableRow): string[] => [
  ...(row.overrides?.name ? ['name'] : []),
  ...(row.overrides?.description ? ['description'] : []),
]

/** The runner's name for a row it has not named yet; only such a row takes the name the description writer gives. */
export const isPlaceholderExtra = (row: ExtraVariableRow) =>
  !row.overrides?.name && /^note(_\d+)?$/.test(nameOf(row))

/** A row the fill made holds the line it was heard in, under the placeholder name, until described. */
export const needsExtraBrief = (row: ExtraVariableRow) =>
  !!nameOf(row) &&
  (isPlaceholderExtra(row) || (!row.overrides?.description && !row.variable_instructions?.trim()))

export const extraRequestRows = (rows: ExtraVariableRow[]) =>
  rows.flatMap((row) =>
    nameOf(row)
      ? [
          {
            variable_name: nameOf(row),
            variable_instructions: row.variable_instructions ?? '',
            authored: extraAuthoredOf(row),
            source: row.source ?? '',
          },
        ]
      : [],
  )

const unclaimed = (proposed: ExtraVariableRow[], taken: Set<string>) =>
  proposed.filter((row) => {
    if (!nameOf(row) || taken.has(nameOf(row))) return false
    taken.add(nameOf(row))
    return true
  })

/** A first fill: the existing extra rows stay, the runner was sent them and proposes none of them again. */
export const fillExtras = (
  rows: ExtraVariableRow[],
  proposed: ExtraVariableRow[] | undefined,
  askedNames: string[],
): ExtraVariableRow[] => [
  ...rows,
  ...unclaimed(proposed ?? [], new Set([...askedNames, ...rows.map(nameOf)])),
]

/** An edit: the rows Jev is sure the edit dropped go, and a line the edit starts noting is added. */
export const applyExtraChanges = (
  rows: ExtraVariableRow[],
  answer: Pick<
    RCollectionSuggestion,
    'extract_only' | 'extract_only_dropped' | 'extract_only_moved'
  >,
  askedNames: string[],
): ExtraVariableRow[] => {
  const dropped = new Set(answer.extract_only_dropped ?? [])
  const moved = answer.extract_only_moved ?? {}
  const kept = rows
    .filter((row) => !dropped.has(nameOf(row)))
    .map((row) => (moved[nameOf(row)] ? { ...row, source: moved[nameOf(row)] } : row))
  return [
    ...kept,
    ...unclaimed(answer.extract_only ?? [], new Set([...askedNames, ...kept.map(nameOf)])),
  ]
}

/** The rows a save keeps: a fully blank one goes, one with only a description gets a placeholder the namer replaces. */
export const namedForDescribe = (rows: ExtraVariableRow[]): ExtraVariableRow[] => {
  const taken = new Set(rows.flatMap((row) => (nameOf(row) ? [nameOf(row)] : [])))
  return rows.flatMap((row) => {
    if (nameOf(row)) return [row]
    if (!row.variable_instructions?.trim()) return []
    let n = 0
    while (taken.has(n ? `note_${n}` : 'note')) n++
    const name = n ? `note_${n}` : 'note'
    taken.add(name)
    return [{ ...row, variable_name: name }]
  })
}

/** A describe answer onto the rows still waiting for it; what the author typed stays, and no asked row's name is taken. */
export const withExtraDescriptions = (
  rows: ExtraVariableRow[],
  { descriptions = {}, names = {} }: Pick<RCollectionSuggestion, 'descriptions' | 'names'>,
  askedNames: string[] = [],
): ExtraVariableRow[] =>
  rows.map((row) => {
    if (!needsExtraBrief(row)) return row
    const brief = row.overrides?.description ? undefined : descriptions[nameOf(row)]
    const to = names[nameOf(row)]
    const name = isPlaceholderExtra(row) && to && !askedNames.includes(to) ? to : undefined
    return {
      ...row,
      ...(brief ? { variable_instructions: brief } : {}),
      ...(name ? { variable_name: name } : {}),
    }
  })

/** The row as phlo-core stores it: the V2 row keys and the author's marks, nothing the builder keeps for itself. */
export const toSavedExtra = (row: ExtraVariableRow): ExtraVariableRow => pick(row, SAVED_KEYS)
